import { SelectOption } from '@lbcamden/lib-react';
import { getSortFn } from '@lbcamden/shared-utils';
import { Queries } from 'tinybase';
import { WsSynchronizer } from 'tinybase/synchronizers/synchronizer-ws-client';
import { MergeableStore } from 'tinybase/with-schemas';

import { usedNoteTypes } from '~/common/config/notes';
import { BaseGlobalStore } from '~/common/tinybase/BaseGlobalStore';
import { ReferenceListItem, ReferenceListType } from '~/common/types/data';
import { UsedNoteType } from '~/common/types/notes';
import { GlobalStoreSchemas, PathPersister } from '~/common/types/tinybase';
import { getPartiallyNumericSortKey } from '~/common/utils/sort';

type SelectOptionLists = Record<ReferenceListType, SelectOption[]>;

type ReferenceListFilter = (item: ReferenceListItem) => boolean;

export const referenceListSelectOptionFilters: Partial<
  Record<ReferenceListType, ReferenceListFilter>
> = {
  COMBNOTOBJ: (item) => usedNoteTypes.includes(item.code as UsedNoteType),
  FLOORLEVEL: (item) => !/\//.test(item.code), // https://lbcamden.atlassian.net/browse/GTP-1019
  LWP_DEMAND: (item) => !/Rent trigger/i.test(item.description),
};

export const referenceListSelectOptionSortKeyFns: Partial<
  Record<ReferenceListType, (option: SelectOption) => unknown>
> = {
  FLOORLEVEL: ({ label }) => {
    const sortKey = getPartiallyNumericSortKey(typeof label === 'string' ? label : '');
    // Sort values that begin with letters first e.g. "Basement", "Ground"
    return /^\d/.test(sortKey) ? `z${sortKey}` : sortKey;
  },
};

export class ClientGlobalStore extends BaseGlobalStore {
  protected referenceListSelectOptionLists = <SelectOptionLists>{};

  public constructor(
    store: MergeableStore<GlobalStoreSchemas>,
    persister: PathPersister | undefined,
    public readonly synchronizer: WsSynchronizer<WebSocket>,
    public readonly queries: Queries,
  ) {
    super(store, persister, synchronizer);

    this.updateReferenceListSelectOptions();

    this.store.addTableListener('referenceLists', () => this.updateReferenceListSelectOptions());
  }

  public async startAutoLoad() {
    await this.synchronizer?.startAutoLoad(this.store.getContent());
  }

  protected updateReferenceListSelectOptions() {
    const selectOptionLists = <SelectOptionLists>{};
    const referenceLists = Object.values(this.getTable('referenceLists')) as ReferenceListItem[];

    for (const item of referenceLists) {
      const filter = referenceListSelectOptionFilters[item.type];

      if (!item.isActive || (filter && !filter(item))) {
        continue;
      }
      const { type, code, description } = item;

      selectOptionLists[type] ||= [];
      selectOptionLists[type].push({
        id: code,
        value: code,
        label: this.formatReferenceItemDescription(type, description),
      });
    }

    // Sort all lists via their labels
    Object.entries(selectOptionLists).forEach(([type, list]) =>
      list.sort(
        getSortFn(
          'asc',
          referenceListSelectOptionSortKeyFns[type as ReferenceListType] || (({ label }) => label),
        ),
      ),
    );

    this.referenceListSelectOptionLists = selectOptionLists;
  }

  public referenceListSelectOptions = <TValue extends string>(type: ReferenceListType) =>
    <SelectOption<TValue>[]>(this.referenceListSelectOptionLists[type] || []);
}
